import { useState, useEffect } from 'react';
import { AdminService, AdminCounts } from '@/services/adminService';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useCompany } from '@/contexts/CompanyContext';
import type { FilterState } from '@/types/filterTypes';

interface SurveySubmission {
  id: string;
  full_name: string;
  email: string;
  profile_name?: string | null;
  scores: any;
  selected_words: any;
  selected_words_text: any;
  completed_at: string;
  created_at: string;
  cached_insights?: any;
}

interface IdealProfile {
  id: string;
  name: string;
  dominance_min: number;
  dominance_max: number;
  extraversion_min: number;
  extraversion_max: number;
  patience_min: number;
  patience_max: number;
  formality_min: number;
  formality_max: number;
  objectivity_min: number;
  objectivity_max: number;
  created_at: string;
  updated_at: string;
}


export const useAdminData = () => {
  const { toast } = useToast();
  const { currentCompany, loading: companyLoading } = useCompany();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchingSearch, setFetchingSearch] = useState(false);
  const [dataLoadError, setDataLoadError] = useState<string | null>(null);
  const [totalCounts, setTotalCounts] = useState<AdminCounts>({ behavioral: 0, idealProfiles: 0 });
  const [behavioralSubmissions, setBehavioralSubmissions] = useState<SurveySubmission[]>([]);
  const [workBehaviorSubmissions, setWorkBehaviorSubmissions] = useState<SurveySubmission[]>([]);
  const [idealProfiles, setIdealProfiles] = useState<IdealProfile[]>([]);
  const [filteredBehavioralCount, setFilteredBehavioralCount] = useState(0);
  const [filteredWorkBehaviorCount, setFilteredWorkBehaviorCount] = useState(0);
  const [deletingProfileIds, setDeletingProfileIds] = useState<Set<string>>(new Set());

  // Reset data when company changes - only trigger once when company is ready
  useEffect(() => {
    // Guard: Don't reset if company is still loading or no company selected
    if (companyLoading || !currentCompany?.company_id) {
      return;
    }
    
    console.log('useAdminData: Company ready:', currentCompany?.company_name, currentCompany?.company_id);
    
    // Set loading to true when company changes
    setLoading(true);
    
    setTotalCounts({ behavioral: 0, idealProfiles: 0 });
    setBehavioralSubmissions([]);
    setWorkBehaviorSubmissions([]);
    setIdealProfiles([]);
    setFilteredBehavioralCount(0);
    setFilteredWorkBehaviorCount(0);
    setDataLoadError(null);
  }, [companyLoading, currentCompany?.company_id]);

  // Real-time updates for work behavior submissions
  useEffect(() => {
    const channel = supabase
      .channel('work-behavior-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'survey_submissions',
          filter: 'assessment_type=eq.work_behavior'
        },
        (payload) => {
          console.log('New work behavior submission detected:', payload.new);
          // Update total counts
          setTotalCounts(prev => ({ ...prev, behavioral: prev.behavioral + 1 }));
          // Trigger a toast notification
          toast({
            title: "New Assessment",
            description: "A new work behavior assessment has been completed.",
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'survey_submissions',
          filter: 'assessment_type=eq.work_behavior'
        },
        (payload) => {
          console.log('Work behavior submission updated:', payload.new);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [toast]);

  const fetchTotalCounts = async () => {
    try {
      setDataLoadError(null);
      console.log('Fetching total counts for company:', currentCompany?.company_name, currentCompany?.company_id);
      
      const counts = await AdminService.getTotalCounts(currentCompany?.company_id);
      console.log('Total counts fetched:', counts);
      
      setTotalCounts(counts);
    } catch (error: any) {
      console.error('Error fetching total counts:', error);
      setDataLoadError(`Failed to load data counts: ${error.message}`);
      toast({
        title: "Data Load Error",
        description: "Failed to fetch data counts. Please check your connection and try again.",
        variant: "destructive"
      });
    }
  };

  const fetchBehavioralData = async (
    page: number, 
    pageSize: number, 
    searchTerm?: string,
    filters?: FilterState
  ) => {
    try {
      console.log('Fetching candidate assessments (unified view) for company:', currentCompany?.company_name, { page, pageSize, searchTerm, filters });
      
      const result = await AdminService.getCandidateAssessments(page, pageSize, searchTerm, filters, currentCompany?.company_id);
      
      // Map view fields to the UI format expected by components
      const mappedData = result.data.map((item: any) => {
        // Calculate cognitive duration from timer (time_remaining), not timestamps
        let cognitiveDuration: number | null = null;
        if (item.has_cognitive && item.cognitive_time_remaining != null) {
          const timeLimitSeconds = 12 * 60; // default 12 minutes
          cognitiveDuration = Math.max(0, timeLimitSeconds - item.cognitive_time_remaining);
        }
        
        return {
          ...item,
          cognitivePercentile: item.cognitive_percentile || null,
          cognitiveDuration,
          isCognitiveOnly: item.has_cognitive && !item.scores,
          status: item.status || (item.has_cognitive && !item.scores ? 'Screening' : null),
        };
      });
      
      setBehavioralSubmissions(mappedData);
      setFilteredBehavioralCount(result.count);
      
      console.log('Candidate assessments fetched:', { count: mappedData.length, totalCount: result.count });
    } catch (error: any) {
      console.error('Error fetching candidate assessments:', error);
      throw error;
    } finally {
      setFetchingSearch(false);
    }
  };

  const fetchWorkBehaviorData = async (
    page: number, 
    pageSize: number, 
    searchTerm?: string,
    filters?: FilterState
  ) => {
    try {
      console.log('Fetching work behavior data for company:', currentCompany?.company_name, { page, pageSize, searchTerm, filters });
      
      const result = await AdminService.getWorkBehaviorSubmissions(page, pageSize, searchTerm, filters, currentCompany?.company_id);
      
      setWorkBehaviorSubmissions(result.data);
      setFilteredWorkBehaviorCount(result.count);
      
      console.log('Work behavior data fetched:', { count: result.data.length, totalCount: result.count });
    } catch (error: any) {
      console.error('Error fetching work behavior submissions:', error);
      throw error;
    }
  };

  const fetchIdealProfilesData = async (page: number, pageSize: number) => {
    try {
      console.log('Fetching ideal profiles data for company:', currentCompany?.company_name, { page, pageSize });
      
      const result = await AdminService.getIdealProfiles(page, pageSize, currentCompany?.company_id);
      
      setIdealProfiles(result.data);
      
      console.log('Ideal profiles data fetched:', { count: result.data.length });
    } catch (error: any) {
      console.error('Error fetching ideal profiles:', error);
      throw error;
    }
  };

  const updateSubmissionStatus = async (submissionId: string, newStatus: string) => {
    // Store previous state for potential rollback
    const previousSubmissions = [...behavioralSubmissions];
    
    // Optimistic update: Update local state immediately
    setBehavioralSubmissions(prev => 
      prev.map(submission => 
        submission.id === submissionId 
          ? { ...submission, status: newStatus }
          : submission
      )
    );

    try {
      // Update database
      const { error } = await supabase
        .from('survey_submissions')
        .update({ status: newStatus })
        .eq('id', submissionId);

      if (error) {
        console.error('Database error:', error);
        
        // Rollback optimistic update on error
        setBehavioralSubmissions(previousSubmissions);
        
        // Handle specific error types with better user messaging
        if (error.code === 'PGRST301' || error.message?.includes('row-level security')) {
          toast({
            title: "Permission Denied",
            description: "You don't have permission to update candidate status. Please check with your administrator.",
            variant: "destructive"
          });
        } else if (error.code === '23503') {
          toast({
            title: "Invalid Status",
            description: "The selected status is not valid for this candidate.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Update Failed",
            description: `Failed to update status: ${error.message}`,
            variant: "destructive"
          });
        }
        throw error;
      }

      console.log('Status updated successfully');
      
      toast({
        title: "Status Updated",
        description: `Status changed to ${newStatus}`,
      });
    } catch (error: any) {
      console.error('Error updating status:', error);
      
      // Rollback optimistic update on error
      setBehavioralSubmissions(previousSubmissions);
      
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
      throw error;
    }
  };

  const removeIdealProfileOptimistically = (profileId: string) => {
    console.log('Removing profile optimistically:', profileId);
    setIdealProfiles(prev => prev.filter(profile => profile.id !== profileId));
    setTotalCounts(prev => ({ ...prev, idealProfiles: prev.idealProfiles - 1 }));
  };

  const restoreIdealProfile = (profile: IdealProfile) => {
    console.log('Restoring profile after failed deletion:', profile.id);
    setIdealProfiles(prev => [...prev, profile].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    ));
    setTotalCounts(prev => ({ ...prev, idealProfiles: prev.idealProfiles + 1 }));
  };

  const setProfileDeleting = (profileId: string, isDeleting: boolean) => {
    setDeletingProfileIds(prev => {
      const newSet = new Set(prev);
      if (isDeleting) {
        newSet.add(profileId);
      } else {
        newSet.delete(profileId);
      }
      return newSet;
    });
  };

  // Optimistic removal for candidate submissions
  const removeSubmissionOptimistically = (submissionId: string) => {
    console.log('Removing submission optimistically:', submissionId);
    setBehavioralSubmissions(prev => prev.filter(sub => sub.id !== submissionId));
    setWorkBehaviorSubmissions(prev => prev.filter(sub => sub.id !== submissionId));
    setTotalCounts(prev => ({ ...prev, behavioral: Math.max(0, prev.behavioral - 1) }));
    setFilteredBehavioralCount(prev => Math.max(0, prev - 1));
    setFilteredWorkBehaviorCount(prev => Math.max(0, prev - 1));
  };

  const refreshAllData = async (page: number, pageSize: number, searchTerm?: string) => {
    setRefreshing(true);
    console.log('Manual refresh triggered');
    
    try {
      await fetchTotalCounts();
      await Promise.all([
        fetchBehavioralData(page, pageSize, searchTerm),
        fetchIdealProfilesData(page, pageSize)
      ]);
    } catch (error: any) {
      console.error('Error during manual refresh:', error);
      setDataLoadError(`Failed to refresh data: ${error.message}`);
      toast({
        title: "Data Load Error",
        description: "Failed to refresh admin data. Please check your permissions and try again.",
        variant: "destructive"
      });
    } finally {
      setRefreshing(false);
    }
  };

  return {
    loading,
    setLoading,
    refreshing,
    fetchingSearch,
    setFetchingSearch,
    dataLoadError,
    totalCounts,
    behavioralSubmissions,
    workBehaviorSubmissions,
    idealProfiles,
    filteredBehavioralCount,
    filteredWorkBehaviorCount,
    deletingProfileIds,
    fetchTotalCounts,
    fetchBehavioralData,
    fetchWorkBehaviorData,
    fetchIdealProfilesData,
    updateSubmissionStatus,
    removeIdealProfileOptimistically,
    restoreIdealProfile,
    setProfileDeleting,
    removeSubmissionOptimistically,
    refreshAllData
  };
};
